// ======================================================================
// FILE:
// src/modules/finance/reports/profit_loss/pages/ProfitLossPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Profit & Loss
//
// PURPOSE:
// Read-only Profit & Loss report.
//
// FEATURES:
// - Company filter
// - Financial Year filter
// - Financial Period filter
// - Full Financial Year option
// - Revenue details
// - Expense details
// - Net Profit / Loss
// ======================================================================

import React, {
    useCallback,
    useMemo,
    useState,
} from "react";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import profitLossService
    from "../../../services/profitLossService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function ProfitLossPage() {

    // ==============================================================
    // STATE
    // ==============================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        revenueRows,
        setRevenueRows,
    ] = useState([]);


    const [
        expenseRows,
        setExpenseRows,
    ] = useState([]);


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");


    const [
        reportData,
        setReportData,
    ] = useState(null);


    // ==============================================================
    // LOOKUP STATE
    // ==============================================================

    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);


    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);


    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);


    // ==============================================================
    // FILTERS
    // ==============================================================

    const [
        filters,
        setFilters,
    ] = useState({

        company_id: null,

        financial_year_id: null,

        financial_period_id: null,

    });


    // ==============================================================
    // UPDATE FILTER
    // ==============================================================

    const updateFilter = useCallback(
        (
            field,
            value
        ) => {

            setFilters(
                previous => ({
                    ...previous,
                    [field]: value,
                })
            );

        },
        []
    );


    // ==============================================================
    // COMPANY LOOKUP
    // ==============================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name ||
                        company.name ||
                        `Company ${company.id}`,

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL YEAR LOOKUP
    // ==============================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchFinancialYears(
                        inputValue
                    );

            return (
                response || []
            ).map(
                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL PERIOD LOOKUP
    // ==============================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !filters.financial_year_id
        ) {

            return [];

        }

        try {

            const response =
                await financeLookupService
                    .searchFinancialPeriods(
                        filters.financial_year_id,
                        inputValue
                    );

            return [

                {
                    value: null,

                    label:
                        "Full Financial Year",

                    data: null,
                },

                ...(response || []).map(
                    period => ({

                        value:
                            period.id,

                        label:
                            period.period_name ||
                            period.period_code ||
                            `Period ${period.id}`,

                        data:
                            period,

                    })
                ),

            ];

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // LOAD PROFIT & LOSS
    // ==============================================================

    const loadProfitLoss = async () => {

        setLoading(true);

        setErrorMessage("");

        try {

            const response =
                await profitLossService
                    .getProfitLoss(
                        filters
                    );


            setReportData(
                response || null
            );


            setRevenueRows(
                response?.revenue?.accounts ||
                []
            );


            setExpenseRows(
                response?.expenses?.accounts ||
                []
            );

        } catch (error) {

            console.error(
                "Failed to load Profit & Loss:",
                error
            );


            setReportData(
                null
            );


            setRevenueRows(
                []
            );


            setExpenseRows(
                []
            );


            setErrorMessage(
                error?.response?.data?.message ||
                "Unable to load Profit & Loss."
            );

        } finally {

            setLoading(false);

        }

    };


    // ==============================================================
    // SEARCH
    // ==============================================================

    const handleSearch = () => {

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select Company."
            );

            return;

        }


        if (
            !filters.financial_year_id
        ) {

            setErrorMessage(
                "Please select Financial Year."
            );

            return;

        }


        loadProfitLoss();

    };


    // ==============================================================
    // RESET
    // ==============================================================

    const handleReset = () => {

        setFilters({

            company_id: null,

            financial_year_id: null,

            financial_period_id: null,

        });


        setSelectedCompany(
            null
        );


        setSelectedFinancialYear(
            null
        );


        setSelectedFinancialPeriod(
            null
        );


        setReportData(
            null
        );


        setRevenueRows(
            []
        );


        setExpenseRows(
            []
        );


        setErrorMessage(
            ""
        );

    };


    // ==============================================================
    // FORMAT AMOUNT
    // ==============================================================

    const formatAmount = (
        value
    ) => {

        const amount =
            Number(
                value || 0
            );


        return amount.toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    };


    // ==============================================================
    // SUMMARY CARDS
    // ==============================================================

    const summaryCards = useMemo(
        () => [

            {
                title:
                    "Total Revenue",

                value:
                    formatAmount(
                        reportData
                            ?.revenue
                            ?.total
                    ),
            },


            {
                title:
                    "Total Expenses",

                value:
                    formatAmount(
                        reportData
                            ?.expenses
                            ?.total
                    ),
            },


            {
                title:
                    "Net Profit / Loss",

                value:
                    formatAmount(
                        Math.abs(
                            Number(
                                reportData
                                    ?.net_profit_loss ||
                                0
                            )
                        )
                    ),
            },


            {
                title:
                    "Result",

                value:
                    reportData?.result ||
                    "-",
            },

        ],
        [
            reportData
        ]
    );


    // ==============================================================
    // RENDER
    // ==============================================================

    return (

        <div className="container-fluid">

            {/* ======================================================
                HEADER
            ====================================================== */}

            <FinancePageHeader
                title="Profit & Loss"
                subtitle="Profit & Loss Report"
            />


            {/* ======================================================
                SUMMARY
            ====================================================== */}

            <FinanceSummaryCards
                cards={
                    summaryCards
                }
            />


            {/* ======================================================
                FILTER PANEL
            ====================================================== */}

            <FinanceSearchPanel
                loading={
                    loading
                }
                onSearch={
                    handleSearch
                }
                onReset={
                    handleReset
                }
            >

                {/* ==================================================
                    COMPANY
                ================================================== */}

                <div className="col-lg-4 col-md-6">

                    <label className="form-label">

                        Company

                        <span className="text-danger">
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadCompanies
                        }
                        isClearable
                        value={
                            selectedCompany
                        }
                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL YEAR
                ================================================== */}

                <div className="col-lg-4 col-md-6">

                    <label className="form-label">

                        Financial Year

                        <span className="text-danger">
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialYears
                        }
                        isClearable
                        value={
                            selectedFinancialYear
                        }
                        onChange={
                            option => {

                                setSelectedFinancialYear(
                                    option
                                );


                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "financial_year_id",
                                    option?.value ||
                                    null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL PERIOD
                ================================================== */}

                <div className="col-lg-4 col-md-6">

                    <label className="form-label">

                        Financial Period

                        <span className="text-muted ms-1">
                            (Optional)
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialPeriods
                        }
                        isClearable
                        isDisabled={
                            !filters.financial_year_id
                        }
                        value={
                            selectedFinancialPeriod
                        }
                        onChange={
                            option => {

                                setSelectedFinancialPeriod(
                                    option
                                );


                                updateFilter(
                                    "financial_period_id",
                                    option?.value ||
                                    null
                                );

                            }
                        }
                    />

                </div>

            </FinanceSearchPanel>


            {/* ======================================================
                ERROR
            ====================================================== */}

            {
                errorMessage && (

                    <div className="alert alert-danger mt-3">

                        {errorMessage}

                    </div>

                )
            }


            {/* ======================================================
                PROFIT / LOSS STATUS
            ====================================================== */}

            {
                reportData && (

                    <div className="card mt-3">

                        <div className="card-body">

                            <div
                                className={
                                    reportData.result === "PROFIT"
                                        ? "alert alert-success mb-0"
                                        : reportData.result === "LOSS"
                                            ? "alert alert-danger mb-0"
                                            : "alert alert-info mb-0"
                                }
                            >

                                <strong>

                                    Profit & Loss:

                                </strong>

                                {" "}

                                {
                                    reportData.result ||
                                    "-"
                                }

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ======================================================
                REVENUE
            ====================================================== */}

            <div className="card mt-3">

                <div className="card-header">

                    <strong>
                        Revenue
                    </strong>

                </div>


                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table
                            className={
                                "table " +
                                "table-hover " +
                                "table-bordered " +
                                "mb-0"
                            }
                        >

                            <thead>

                                <tr>

                                    <th>
                                        Account Code
                                    </th>

                                    <th>
                                        Account Name
                                    </th>

                                    <th>
                                        Category
                                    </th>

                                    <th className="text-end">
                                        Debit
                                    </th>

                                    <th className="text-end">
                                        Credit
                                    </th>

                                    <th className="text-end">
                                        Amount
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                Loading Profit & Loss...

                                            </td>

                                        </tr>

                                    ) : revenueRows.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                No Revenue data found.

                                            </td>

                                        </tr>

                                    ) : (

                                        revenueRows.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.account_id ||
                                                        index
                                                    }
                                                >

                                                    <td>
                                                        {
                                                            row.account_code ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td>
                                                        {
                                                            row.account_name ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td>
                                                        {
                                                            row.account_category ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.debit
                                                            )
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.credit
                                                            )
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.amount
                                                            )
                                                        }
                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>


                            {/* ==================================================
                                REVENUE TOTAL
                            ================================================== */}

                            {
                                reportData && (

                                    <tfoot>

                                        <tr>

                                            <th
                                                colSpan="5"
                                                className="text-end"
                                            >

                                                Total Revenue

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.revenue
                                                            ?.total
                                                    )
                                                }

                                            </th>

                                        </tr>

                                    </tfoot>

                                )
                            }

                        </table>

                    </div>

                </div>

            </div>


            {/* ======================================================
                EXPENSES
            ====================================================== */}

            <div className="card mt-3">

                <div className="card-header">

                    <strong>
                        Expenses
                    </strong>

                </div>


                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table
                            className={
                                "table " +
                                "table-hover " +
                                "table-bordered " +
                                "mb-0"
                            }
                        >

                            <thead>

                                <tr>

                                    <th>
                                        Account Code
                                    </th>

                                    <th>
                                        Account Name
                                    </th>

                                    <th>
                                        Category
                                    </th>

                                    <th className="text-end">
                                        Debit
                                    </th>

                                    <th className="text-end">
                                        Credit
                                    </th>

                                    <th className="text-end">
                                        Amount
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                Loading Profit & Loss...

                                            </td>

                                        </tr>

                                    ) : expenseRows.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className={
                                                    "text-center " +
                                                    "py-4"
                                                }
                                            >

                                                No Expense data found.

                                            </td>

                                        </tr>

                                    ) : (

                                        expenseRows.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.account_id ||
                                                        index
                                                    }
                                                >

                                                    <td>
                                                        {
                                                            row.account_code ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td>
                                                        {
                                                            row.account_name ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td>
                                                        {
                                                            row.account_category ||
                                                            "-"
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.debit
                                                            )
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.credit
                                                            )
                                                        }
                                                    </td>


                                                    <td className="text-end">
                                                        {
                                                            formatAmount(
                                                                row.amount
                                                            )
                                                        }
                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>


                            {/* ==================================================
                                EXPENSE TOTAL
                            ================================================== */}

                            {
                                reportData && (

                                    <tfoot>

                                        <tr>

                                            <th
                                                colSpan="5"
                                                className="text-end"
                                            >

                                                Total Expenses

                                            </th>


                                            <th
                                                className="text-end"
                                            >

                                                {
                                                    formatAmount(
                                                        reportData
                                                            ?.expenses
                                                            ?.total
                                                    )
                                                }

                                            </th>

                                        </tr>

                                    </tfoot>

                                )
                            }

                        </table>

                    </div>

                </div>

            </div>


            {/* ======================================================
                NET PROFIT / LOSS
            ====================================================== */}

            {
                reportData && (

                    <div className="card mt-3 mb-4">

                        <div className="card-body">

                            <div className="row">

                                <div className="col-md-8 text-end">

                                    <strong>

                                        {
                                            reportData.result === "LOSS"
                                                ? "Net Loss"
                                                : "Net Profit"
                                        }

                                    </strong>

                                </div>


                                <div className="col-md-4 text-end">

                                    <strong>

                                        {
                                            formatAmount(
                                                Math.abs(
                                                    Number(
                                                        reportData
                                                            ?.net_profit_loss ||
                                                        0
                                                    )
                                                )
                                            )
                                        }

                                    </strong>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}